import { ActionIcon, Icon } from "@/components"
import { useStyles } from "@/components/PillGroup/PillGroup.styles"
import { useMantineTheme } from "@mantine/core"
import React from "react"

export interface PillGroupProps<T> {
    // the data.item property to use as label
    labelProp?: string
    // the data.item property to use as key
    keyProp?: keyof T
    data: T[]
    onClose: (data: T[]) => void
    onEdit?: (item: T, index: number) => void
    labelRender?: (item: T) => string | React.ReactNode
}

export function PillGroup<T>({ data, labelProp = "value", keyProp, onEdit, labelRender, ...props }: PillGroupProps<T>) {
    const { classes } = useStyles()
    const theme = useMantineTheme()

    const onRemove = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, key: T | T[keyof T]) => {
        e.stopPropagation()
        props.onClose(
            data.filter((item) => {
                if (keyProp) {
                    return item[keyProp] !== key
                } else {
                    return item !== key
                }
            }),
        )
    }

    const renderPills = (): React.ReactNode =>
        data?.map((item, index) => {
            if (item) {
                return (
                    <div
                        key={(item[labelProp] || item) + index}
                        role="button"
                        tabIndex={onEdit ? 0 : -1}
                        className={classes.pill}
                        onClick={() => onEdit?.(item, index)}
                        onKeyDown={() => onEdit?.(item, index)}
                        aria-label="Custom button"
                    >
                        {labelRender ? labelRender(item) : item[labelProp] || item}
                        <ActionIcon
                            className={classes.actionIcon}
                            onClick={(e) => onRemove(e, keyProp ? item[keyProp] : item)}
                            variant="transparent"
                        >
                            <Icon icon="times" iconFamily="far" color={theme.colors.gray[7]} />
                        </ActionIcon>
                    </div>
                )
            } else {
                return <></>
            }
        })

    return <div className={classes.container}>{renderPills()}</div>
}
